import type { SyntheticEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { copyText } from '@/utils/dom-utils';

type UseClipboardOptions = {
    /**
     * Duration in milliseconds to keep the copied state true.
     * Defaults to 1500ms.
     */
    timeoutMs?: number;
    /**
     * Optional callback invoked when copying fails.
     */
    onError?: (error: unknown) => void;
};

type CopyOptions = {
    event?: SyntheticEvent;
    /**
     * Prevent the originating event from bubbling to parent handlers.
     * Defaults to true when an event is provided.
     */
    suppressPropagation?: boolean;
};

export const useClipboard = ({ timeoutMs = 1500, onError }: UseClipboardOptions = {}) => {
    const [isCopied, setIsCopied] = useState(false);
    const resetTimerRef = useRef<number | null>(null);

    const clearTimer = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (resetTimerRef.current !== null) {
            window.clearTimeout(resetTimerRef.current);
            resetTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            clearTimer();
        };
    }, [clearTimer]);

    const copy = useCallback(
        async (value: string, options?: CopyOptions) => {
            const { event, suppressPropagation = true } = options ?? {};

            if (event && suppressPropagation && 'stopPropagation' in event) {
                event.stopPropagation();
            }

            clearTimer();

            try {
                const didCopy = await copyText(value);
                if (!didCopy) {
                    throw new Error('Clipboard copy failed.');
                }
                setIsCopied(true);

                if (typeof window !== 'undefined') {
                    resetTimerRef.current = window.setTimeout(() => {
                        setIsCopied(false);
                        resetTimerRef.current = null;
                    }, timeoutMs);
                }

                return true;
            } catch (error) {
                onError?.(error);
                return false;
            }
        },
        [clearTimer, onError, timeoutMs],
    );

    return {
        isCopied,
        copy,
    };
};
